import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import throttle from 'lodash/throttle';
import { LightDomReparentController } from '@/controllers/LightDomReparentController.js';
import { dispatchFilterEvent, FILTER_EVENTS, FILTER_PRESENTATIONS } from '@/components/filters/constants.js';
import styles from './filters-collapse.scss?inline';

const DEFAULT_FORCE_COLLAPSE_MQ = '(max-width: 1024px)';
const LAYOUT_THROTTLE_MS = 80;
const OVERFLOW_EPSILON_PX = 1;
/** Max rAF retries waiting for host width / group metrics before first measure. */
const BOOTSTRAP_LAYOUT_MAX_FRAMES = 16;

const BUCKETS = Object.freeze({
    GROUPS: 'groups',
    APPLY: 'apply',
    RESET: 'reset',
});

/**
 * Responsive layout host for filter groups.
 *
 * @fires filter-change - detail: { name, option, value, checked, originalEvent }
 * @fires filter-apply - detail: { name: null }
 * @fires filter-reset - detail: { name: null }
 * @fires filter-open - detail: { name: null }
 * @fires filter-close - detail: { name: null }
 * @fires filter-collapse-change - detail: { name: null, collapsed }
 */
export default class SotbitFiltersCollapse extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        forceCollapseMq: { type: String, attribute: 'force-collapse-mq' },
        collapsedLabel: { type: String, attribute: 'collapsed-label' },
        collapsed: { type: Boolean, reflect: true },
        count: { type: Number, reflect: true },
        layoutReady: { type: Boolean, reflect: true, attribute: 'layout-ready' },
    };

    constructor() {
        super();
        this.forceCollapseMq = DEFAULT_FORCE_COLLAPSE_MQ;
        this.collapsedLabel = 'Filters';
        this.collapsed = false;
        this.count = 0;
        this.layoutReady = false;

        this._dropdownRef = createRef();
        this._rowRef = createRef();
        this._panelRef = createRef();
        this._actionsRef = createRef();
        this._nodes = new LightDomReparentController(this);
        this._nodes.listen(BUCKETS.GROUPS, FILTER_EVENTS.CHANGE, this._onGroupChange);
        this._nodes.listen(BUCKETS.APPLY, 'click', this._onApplyClick);
        this._nodes.listen(BUCKETS.RESET, 'click', this._onResetClick);

        this._mediaQuery = null;
        this._resizeObserver = null;
        this._requiredExpandedWidth = 0;
        this._layoutGeneration = 0;
        this._measuring = false;
        this._suppressCollapseEvent = false;
        this._scheduleLayout = throttle(() => this._evaluateLayout(), LAYOUT_THROTTLE_MS, {
            leading: true,
            trailing: true,
        });
    }

    connectedCallback() {
        super.connectedCallback();
        this._nodes.connect();
        this._setupLayoutObservation();

        // Avoid expanded→collapsed flash when MQ already forces collapse.
        if (this._hasForcedCollapse()) {
            this.collapsed = true;
        }

        void this._bootstrapLayout();
    }

    disconnectedCallback() {
        this._layoutGeneration += 1;
        this._scheduleLayout.cancel();
        this._teardownLayoutObservation();
        void this.close();
        this._nodes.disconnect();
        this.layoutReady = false;
        super.disconnectedCallback();
    }

    /**
     * First layout stays invisible until two consistent passes (group metrics settle).
     */
    async _bootstrapLayout() {
        await this.updateComplete;
        await new Promise((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

        for (let attempt = 0; attempt < BOOTSTRAP_LAYOUT_MAX_FRAMES && this.isConnected; attempt += 1) {
            if (this._hasForcedCollapse() || (this._getAvailableWidth() > 0 && this._groupsHaveMetrics())) {
                break;
            }
            await new Promise((resolve) => requestAnimationFrame(resolve));
        }

        // Pass 1: decide while still hidden (do not reveal yet).
        await this._evaluateLayout({ reveal: false });
        await new Promise((resolve) => requestAnimationFrame(resolve));

        // Pass 2: confirm after buttons/fonts settle, then reveal.
        await this._evaluateLayout({ reveal: true });
        await new Promise((resolve) => requestAnimationFrame(resolve));
    }

    _setMeasuring(isMeasuring) {
        this._measuring = isMeasuring;
        this.toggleAttribute('measuring', isMeasuring);
    }

    _markLayoutReady() {
        if (this.isConnected && !this.layoutReady) {
            this.layoutReady = true;
        }
    }

    _getAvailableWidth() {
        const row = this._rowRef.value;
        return Math.floor(row?.clientWidth || this.clientWidth || 0);
    }

    /**
     * Filter-group hosts can report 0×0 for a frame before their dropdown anchors layout.
     */
    _groupsHaveMetrics() {
        const groups = this.getGroups();
        if (groups.length === 0) {
            return false;
        }

        return groups.every((group) => Math.ceil(group.getBoundingClientRect().width) > 0);
    }

    /**
     * Returns a snapshot instead of exposing the component's mutable cache.
     *
     * @returns {HTMLElement[]}
     */
    getGroups() {
        return this._nodes.get(BUCKETS.GROUPS).filter((node) => node.tagName === 'SOTBIT-FILTER-GROUP');
    }

    /**
     * Selected values keyed by group `name` (reads groups even when teleported).
     *
     * @returns {Object<string, string[]>}
     */
    getSelection() {
        const selected = {};

        this.getGroups().forEach((group) => {
            const name = String(group.name || group.getAttribute('name') || '');
            if (!name || typeof group.getSelectedValues !== 'function') {
                return;
            }

            const values = group.getSelectedValues();
            if (values.length) {
                selected[name] = values;
            }
        });

        return selected;
    }

    /**
     * Re-dispatch group option changes from this host so consumers can listen
     * on `sotbit-filters-collapse` even when groups are teleported.
     *
     * @param {CustomEvent} event
     */
    _onGroupChange = (event) => {
        event.stopPropagation();
        dispatchFilterEvent(this, FILTER_EVENTS.CHANGE, {
            ...(event.detail || {}),
        });
    };

    /**
     * @returns {Promise<void>}
     */
    async close() {
        await this._dropdownRef.value?.hide?.();
    }

    _setupLayoutObservation() {
        this._teardownLayoutObservation();

        if (this.forceCollapseMq) {
            this._mediaQuery = window.matchMedia(this.forceCollapseMq);
            this._mediaQuery.addEventListener('change', this._onMediaQueryChange);
        }

        if (typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(() => this._scheduleLayout());
            this._refreshLayoutObservationTargets();
        }
    }

    _refreshLayoutObservationTargets() {
        if (!this._resizeObserver) {
            return;
        }

        this._resizeObserver.disconnect();
        this._resizeObserver.observe(this);
        if (this._rowRef.value) {
            this._resizeObserver.observe(this._rowRef.value);
        }
        this.getGroups().forEach((group) => this._resizeObserver.observe(group));
    }

    _teardownLayoutObservation() {
        this._mediaQuery?.removeEventListener('change', this._onMediaQueryChange);
        this._mediaQuery = null;
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
    }

    _onMediaQueryChange = () => {
        this._scheduleLayout();
    };

    _captureSlot(bucket, slot, predicate = () => true) {
        const nodes = slot.assignedElements({ flatten: true }).filter(predicate);
        this._nodes.capture(bucket, nodes);
        this._syncGroupPresentations();
        this._distribute();
        this._refreshLayoutObservationTargets();

        if (this.layoutReady) {
            this._scheduleLayout();
        } else {
            void this._evaluateLayout({ reveal: false });
        }
    }

    _onGroupsSlotChange = ({ currentTarget }) => {
        this._captureSlot(BUCKETS.GROUPS, currentTarget, (node) => node.tagName === 'SOTBIT-FILTER-GROUP');
    };

    _onApplySlotChange = ({ currentTarget }) => {
        this._captureSlot(BUCKETS.APPLY, currentTarget);
    };

    _onResetSlotChange = ({ currentTarget }) => {
        this._captureSlot(BUCKETS.RESET, currentTarget);
    };

    _syncGroupPresentations() {
        const presentation = this.collapsed ? FILTER_PRESENTATIONS.SECTION : FILTER_PRESENTATIONS.DROPDOWN;

        this.getGroups().forEach((group) => {
            if (group.presentation !== presentation) {
                group.presentation = presentation;
            }
        });
    }

    /**
     * Moves groups into `sotbit-filter-panel` and actions into dropdown footer
     * while collapsed.
     */
    _distribute() {
        if (!this.collapsed) {
            return;
        }

        const panel = this._panelRef.value;
        if (!panel) {
            return;
        }

        this._nodes.move(BUCKETS.GROUPS, panel);

        const actions = this._actionsRef.value;
        if (actions) {
            this._nodes.move(BUCKETS.APPLY, actions);
            this._nodes.move(BUCKETS.RESET, actions);
            actions.hidden =
                this._nodes.get(BUCKETS.APPLY).length + this._nodes.get(BUCKETS.RESET).length === 0;
        }
    }

    _hasForcedCollapse() {
        return Boolean(this._mediaQuery?.matches);
    }

    _rowHasOverflow() {
        const row = this._rowRef.value;
        if (!row) {
            return null;
        }

        const availableWidth = this._getAvailableWidth();
        // Parent may still be display:none / not laid out — do not decide yet.
        if (availableWidth <= 0) {
            return null;
        }

        const groups = this.getGroups();
        const styles = getComputedStyle(row);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;

        // Sum trigger widths, not clamped host boxes. A long property name is
        // max-width: 100% on the group, so the border box fits the row while
        // the label is clipped and later groups are pushed out of view.
        let contentWidth = 0;
        if (groups.length > 0) {
            groups.forEach((group, index) => {
                const triggerWidth =
                    typeof group.getTriggerWidth === 'function'
                        ? group.getTriggerWidth()
                        : Math.ceil(group.getBoundingClientRect().width);
                contentWidth += triggerWidth;
                if (index > 0) {
                    contentWidth += gap;
                }
            });
        } else {
            contentWidth = Math.ceil(row.scrollWidth);
        }

        this._requiredExpandedWidth = contentWidth;

        return contentWidth > availableWidth + OVERFLOW_EPSILON_PX;
    }

    /**
     * Measures expanded layout only when the container has grown enough to fit
     * the last known required width, avoiding resize-time render thrashing.
     *
     * @param {{ reveal?: boolean }} [options]
     */
    async _evaluateLayout(options = {}) {
        const reveal = options.reveal !== false;

        if (!this.isConnected || this._measuring) {
            return;
        }

        const generation = ++this._layoutGeneration;

        if (this._hasForcedCollapse()) {
            if (!this.collapsed) {
                this.collapsed = true;
            }
            if (reveal) {
                this._markLayoutReady();
            }
            return;
        }

        await this.updateComplete;
        if (!this.isConnected || generation !== this._layoutGeneration) {
            return;
        }

        if (!this.collapsed) {
            if (!this._groupsHaveMetrics()) {
                return;
            }

            const overflow = this._rowHasOverflow();
            if (overflow === null) {
                return;
            }

            if (overflow) {
                // Hide before swapping to collapsed so layout-ready hosts don't flash.
                this._setMeasuring(true);
                try {
                    this.collapsed = true;
                    await this.updateComplete;
                } finally {
                    this._setMeasuring(false);
                }
            }

            if (reveal) {
                this._markLayoutReady();
            }
            return;
        }

        const availableWidth = this._getAvailableWidth();
        if (availableWidth <= 0) {
            return;
        }

        if (this._requiredExpandedWidth > 0 && availableWidth + OVERFLOW_EPSILON_PX < this._requiredExpandedWidth) {
            if (reveal) {
                this._markLayoutReady();
            }
            return;
        }

        this._setMeasuring(true);
        this._suppressCollapseEvent = true;
        const initiallyCollapsed = this.collapsed;

        try {
            this.collapsed = false;
            await this.updateComplete;
            await new Promise((resolve) => requestAnimationFrame(resolve));

            if (!this.isConnected || generation !== this._layoutGeneration) {
                return;
            }

            if (!this._groupsHaveMetrics()) {
                this.collapsed = initiallyCollapsed;
                await this.updateComplete;
                return;
            }

            const overflow = this._rowHasOverflow();
            if (overflow === null) {
                this.collapsed = initiallyCollapsed;
                await this.updateComplete;
                return;
            }

            this.collapsed = this._hasForcedCollapse() || overflow;
            await this.updateComplete;
            if (reveal) {
                this._markLayoutReady();
            }
        } finally {
            this._setMeasuring(false);
            this._suppressCollapseEvent = false;
        }

        if (this.isConnected && this.collapsed !== initiallyCollapsed) {
            this._emitCollapseChange();
        }
    }

    _emitCollapseChange() {
        dispatchFilterEvent(this, FILTER_EVENTS.COLLAPSE_CHANGE, {
            name: null,
            collapsed: this.collapsed,
        });
    }

    _onDropdownOpen = () => {
        this.getGroups().forEach((group) => {
            group.restoreCommittedOptions?.();
        });
        dispatchFilterEvent(this, FILTER_EVENTS.OPEN, { name: null });
    };

    _onDropdownClose = () => {
        dispatchFilterEvent(this, FILTER_EVENTS.CLOSE, { name: null });
    };

    _onApplyClick = (event) => {
        event.preventDefault();
        const accepted = dispatchFilterEvent(this, FILTER_EVENTS.APPLY, { name: null });
        if (accepted) {
            this.getGroups().forEach((group) => {
                group.commitOptions?.();
            });
            void this.close();
        }
    };

    _onResetClick = (event) => {
        event.preventDefault();
        const accepted = dispatchFilterEvent(this, FILTER_EVENTS.RESET, { name: null });
        if (accepted) {
            this.getGroups().forEach((group) => {
                group.clearCommittedOptions?.();
                group.restoreCommittedOptions?.();
            });
            void this.close();
        }
    };

    _renderActionCaptureSlots() {
        return html`
            <div class="filters-collapse__capture" hidden>
                <slot name="apply" @slotchange=${this._onApplySlotChange}></slot>
                <slot name="reset" @slotchange=${this._onResetSlotChange}></slot>
            </div>
        `;
    }

    _renderCollapsed() {
        const accessibleLabel = this.count > 0 ? `${this.collapsedLabel} (${this.count})` : this.collapsedLabel;

        return html`
            <sotbit-dropdown
                ${ref(this._dropdownRef)}
                class="filters-collapse__dropdown"
                part="filters-collapse__dropdown"
                variant="dropdown-select"
                max-content
                max-height="450px"
                modal-class="filters-collapse-modal"
                modal-title=${this.collapsedLabel || nothing}
                close-icon="close"
                hide-on-select="false"
                disable-close-on-resize
                use-teleport
                @open=${this._onDropdownOpen}
                @close=${this._onDropdownClose}
            >
                <sotbit-button-dropdown slot="anchor" flat background hide-right-icon aria-label=${accessibleLabel || nothing}>
                    <sotbit-icon slot="icon-left" icon="filter" variant="simple" size="m"></sotbit-icon>
                    <span
                        slot="content"
                        class="filters-collapse__button-title"
                        part="filters-collapse__button-title"
                        data-count=${this.count > 0 ? this.count : nothing}
                    >
                        ${this.collapsedLabel}
                    </span>
                </sotbit-button-dropdown>
                <sotbit-filter-panel ${ref(this._panelRef)} variant="stack"></sotbit-filter-panel>
                <div
                    slot="footer"
                    class="filters-collapse__actions"
                    part="filters-collapse__actions"
                    ${ref(this._actionsRef)}
                    hidden
                ></div>
            </sotbit-dropdown>
            <div class="filters-collapse__capture" hidden>
                <slot @slotchange=${this._onGroupsSlotChange}></slot>
                <slot name="apply" @slotchange=${this._onApplySlotChange}></slot>
                <slot name="reset" @slotchange=${this._onResetSlotChange}></slot>
            </div>
        `;
    }

    _renderExpanded() {
        return html`
            <div class="filters-collapse__row" part="filters-collapse__row" ${ref(this._rowRef)}>
                <slot @slotchange=${this._onGroupsSlotChange}></slot>
            </div>
            ${this._renderActionCaptureSlots()}
        `;
    }

    willUpdate(changedProperties) {
        if (changedProperties.has('collapsed')) {
            void this.close();
            this._nodes.restoreAll();
        }
    }

    updated(changedProperties) {
        if (changedProperties.has('collapsed')) {
            this._syncGroupPresentations();
            this._distribute();
            this._refreshLayoutObservationTargets();

            if (changedProperties.get('collapsed') !== undefined && !this._suppressCollapseEvent) {
                this._emitCollapseChange();
            }
        }

        if (changedProperties.has('forceCollapseMq') && changedProperties.get('forceCollapseMq') !== undefined) {
            this._setupLayoutObservation();
            this._scheduleLayout();
        }
    }

    render() {
        return this.collapsed ? this._renderCollapsed() : this._renderExpanded();
    }
}
